import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { ApiCacheService } from '../service/api-cache.service';

/**
 * A single de-duplication rule.
 * - match: path suffix matched against the request URL pathname (opt-in
 *   whitelist; endsWith — so '/dropdown/bind-act-list' cannot accidentally
 *   capture a future '/dropdown/bind-act-list-by-user')
 * - ttl: cache time-to-live in milliseconds (omitted/0 = no expiration)
 * - coalesce: when true, only collapse concurrent in-flight requests
 *   (the response is NOT cached after completion)
 */
export interface ApiDedupRule {
  match: string;
  ttl?: number;
  coalesce?: boolean;
}

/**
 * Centralized backend endpoint path-suffix constants for the dedup whitelist.
 * Kept out of the rule list below (and out of inline literals) so endpoint
 * mappings live in one named place. These are environment-independent route
 * suffixes (identical across demo/staging/prod), so they belong here as shared
 * constants rather than in environment.ts, which holds environment-specific
 * values only.
 */
export const API_DEDUP_ENDPOINTS = {
  LOCATION_LIST_DROPDOWN: '/dropdown/get-location-list-drop-down',
  USER_ROLES: '/common/get-user-roles',
  FY_DETAIL_COMPLIANCE_DROPDOWN: '/dropdown/fy-detail-compliance-dropdown',
  BIND_ACT_LIST: '/dropdown/bind-act-list',
  USER_CATEGORY_MAPS: '/management/user-category-maps',
  SIDE_BAR_MENU_DETAILS: '/common/side-bar-menu-details',
  CUSTOMIZATION_LIST: '/transaction/customization-list',
  COMPANY_SWITCHER_CUSTOMER_DROPDOWN: '/dropdown/company-switcher-customer-dropdown',
} as const;

/**
 * Opt-in whitelist of reference-data endpoints that are safe to de-duplicate.
 * Runtime monitoring showed these fire 2-4x per post-login session because
 * multiple service wrappers call them without coordination.
 */
export const API_DEDUP_RULES: ReadonlyArray<ApiDedupRule> = [
  // wave 1: pure reference dropdowns
  { match: API_DEDUP_ENDPOINTS.LOCATION_LIST_DROPDOWN, ttl: 5 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.USER_ROLES, ttl: 5 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.FY_DETAIL_COMPLIANCE_DROPDOWN, ttl: 15 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.BIND_ACT_LIST, ttl: 5 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.USER_CATEGORY_MAPS, ttl: 5 * 60_000 },
  // wave 2: per-session data — invalidated on company switch/logout via
  // ApiCacheService.clearAll() (see invalidation note below)
  { match: API_DEDUP_ENDPOINTS.SIDE_BAR_MENU_DETAILS, ttl: 15 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.CUSTOMIZATION_LIST, ttl: 15 * 60_000 },
  { match: API_DEDUP_ENDPOINTS.COMPANY_SWITCHER_CUSTOMER_DROPDOWN, ttl: 15 * 60_000 },
];

/**
 * Extract the pathname from an absolute or relative URL.
 * The base only matters for relative URLs and never leaks into the pathname.
 */
function extractPathname(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url;
  }
}

/**
 * Extract pathname + query string from an absolute or relative URL.
 * The query string participates in the cache key so two POSTs that differ
 * only by query params never collapse onto one cached response.
 */
function extractPathnameWithQuery(url: string): string {
  try {
    const parsed = new URL(url, 'http://localhost');
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

/**
 * Deep-clone a JSON response body so no two subscribers ever share a mutable
 * reference. Safe because this interceptor only handles responseType 'json'.
 */
function cloneJsonBody(body: unknown): unknown {
  if (body === null || typeof body !== 'object') {
    return body;
  }
  return JSON.parse(JSON.stringify(body)) as unknown;
}

/**
 * Whitelist-based API de-duplication interceptor.
 *
 * Sees 100% of HttpClient traffic (unlike ApiService.post, which is bypassed by
 * ReduxApiHandlerService and several direct HttpClient calls) and collapses
 * byte-identical POSTs to whitelisted reference-data endpoints into a single
 * backend request via ApiCacheService (shareReplay + TTL).
 *
 * Cache invalidation: ApiCacheService.clearAll() is called on logout
 * (UserService.resetUserServiceState) and on company switch
 * (LayoutComponent.fetchCustomizationListAndReloadMenu).
 */
export const apiDedupInterceptor: HttpInterceptorFn = (req, next) => {
  if (
    req.method !== 'POST' ||
    req.responseType !== 'json' ||
    req.body instanceof FormData ||
    // Progress consumers (observe: 'events' + reportProgress) bypass dedup so
    // their Sent/UploadProgress/DownloadProgress event contract is preserved —
    // the cache only replays the final HttpResponse.
    req.reportProgress
  ) {
    return next(req);
  }

  const pathname = extractPathname(req.url);
  const rule = API_DEDUP_RULES.find((r) => pathname.endsWith(r.match));
  if (!rule) {
    return next(req);
  }

  const apiCache = inject(ApiCacheService);
  // Key on pathname + query string: query params change request semantics just
  // like the body does, so they must differentiate cache entries.
  const cacheKey = apiCache.generateCacheKey(
    extractPathnameWithQuery(req.urlWithParams),
    req.body
  );

  return apiCache
    .get<HttpResponse<unknown>>(
      cacheKey,
      () =>
        next(req).pipe(
          filter(
            (event): event is HttpResponse<unknown> =>
              event instanceof HttpResponse
          ),
          // Explicit failure contract: rethrow so the error reaches every
          // coalesced subscriber untouched and ApiCacheService evicts the
          // entry — failed responses are never cached or replayed.
          catchError((err: unknown) => throwError(() => err))
        ),
      { ttl: rule.ttl ?? 0, deleteOnComplete: rule.coalesce === true }
    )
    .pipe(
      map(
        (response): HttpEvent<unknown> =>
          response.clone({ body: cloneJsonBody(response.body) })
      )
    );
};
